import { Modal } from '@swtorbit/ui/react';
import type { TrlMapNodeDef, TrlMapValidation } from '@swtorbit/shared-socket-events';

interface GameInfoModalProps {
  nodes: TrlMapNodeDef[];
  validation: TrlMapValidation;
  onClose: () => void;
}

export function GameInfoModal({ nodes, validation, onClose }: GameInfoModalProps) {
  const roundMap = new Map<number, TrlMapNodeDef[]>();
  for (const n of nodes) {
    const level = n.derivedLevel ?? 0;
    if (!roundMap.has(level)) roundMap.set(level, []);
    roundMap.get(level)!.push(n);
  }
  const rounds = [...roundMap.entries()].sort((a, b) => a[0] - b[0]);

  return (
    <Modal open onClose={onClose} className="round-creation-modal" title="Game Info" footer={
<>
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </>
}>
        {rounds.length === 0 ? (
          <p className="text-slate">No planets on the map yet.</p>
        ) : (
          <div className="d-flex flex-column gap-sm">
            {rounds.map(([level, levelNodes]) => (
              <div key={level} className="d-flex flex-column gap-2xs">
                <strong className="fs-sm">
                  {level === 0 ? 'Unconnected' : `Round ${level}`} — {levelNodes.length} planet{levelNodes.length !== 1 ? 's' : ''}
                </strong>
                <table className="w-100 fs-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr className="text-slate fs-xs">
                      <th className="text-start p-2xs">Planet</th>
                      <th className="text-start p-2xs">Type</th>
                      <th className="text-start p-2xs">Images</th>
                      <th className="text-start p-2xs">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {levelNodes.map((n) => (
                      <tr key={n.id} className="border-top border-teal-dark">
                        <td className="p-2xs">{n.label}</td>
                        <td className="p-2xs">
                          {n.roundType === 'triangulation' ? (
                            <div className="d-flex flex-column gap-2xs">
                              <span className="badge badge--blue fs-xs">△ Triangulation</span>
                              {!n.triangulationTarget && (
                                <span className="text-amber fs-xs">⚠ No target</span>
                              )}
                            </div>
                          ) : (
                            <span className="fs-xs text-slate">○ Single</span>
                          )}
                        </td>
                        <td className="p-2xs">
                          {n.images.length === 0 ? (
                            <span className="text-red fs-xs">⚠ None</span>
                          ) : (
                            <div className="d-flex flex-column gap-2xs">
                              {n.images.map((img) => (
                                <span key={img.id} className="fs-xs text-slate">
                                  {img.imageName ?? img.imageId}
                                  {img.imageArea ? ` — ${img.imageArea}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-2xs">
                          <div className="d-flex gap-2xs flex-wrap">
                            {n.isCheckpoint && <span className="badge badge--amber fs-xs">Checkpoint</span>}
                            {n.isStart && <span className="text-green-dark fs-xs">● Start</span>}
                            {n.isFinal && <span className="text-gold fs-xs">★ Final</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex flex-column gap-2xs mt-sm">
          <strong className="fs-sm">Validation</strong>
          {validation.ok ? (
            <div className="p-xs rounded-xs bg-green-dark text-white">✓ Map is valid — ready to start.</div>
          ) : (
            <ul className="m-0 d-flex flex-column gap-2xs">
              {validation.errors.map((err, i) => (
                <li key={`${err.code}-${err.nodeId ?? err.edgeId ?? i}`} className="fs-sm text-red">
                  {err.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
  );
}
